// @file Common enums that we use

enum HttpCode {
  Unauthorized = 401,
  Forbidden = 403,
  NotFound = 404,
  UnprocessableEntity = 422,
  RateLimitError = 429,
  ServerError = 500,
}

// Keeps track of error codes in app/services/api_error_code.rb
enum ApiErrorCode {
  ACTIVE_LIBRARY_ONLY = 'ACTIVE_LIBRARY_ONLY',
  EMAIL_ALREADY_IN_USE = 'EMAIL_ALREADY_IN_USE',
  SUBDOMAIN_ALREADY_IN_USE = 'SUBDOMAIN_ALREADY_IN_USE',
  INVALID_ATTRIBUTE = 'INVALID_ATTRIBUTE',
  INVALID_EMAIL = 'INVALID_EMAIL',
  INVALID_EMAIL_DOMAIN = 'INVALID_EMAIL_DOMAIN',
  INVALID_TOKEN = 'INVALID_TOKEN',
  TOKEN_EXPIRED = 'TOKEN_EXPIRED',
  MISSING_ATTRIBUTE = 'MISSING_ATTRIBUTE',
  NOT_UNIQUE = 'NOT_UNIQUE',
  BACKPACK_ONLY = 'BACKPACK_ONLY',
  NO_USER = 'NO_USER',
  RECORD_INVALID = 'RECORD_INVALID',
  SUSPENDED_ACCOUNT = 'SUSPENDED_ACCOUNT',
  WRONG_PASSWORD = 'WRONG_PASSWORD',
  PASSWORD_TOO_SHORT = 'PASSWORD_TOO_SHORT',
  INVALID_PASSWORD = 'INVALID_PASSWORD',
  PASSWORD_NOT_CHANGED = 'PASSWORD_NOT_CHANGED',
  TOO_MANY_REQUESTS = 'TOO_MANY_REQUESTS',
  USERNAME_ALREADY_IN_USE = 'USERNAME_ALREADY_IN_USE',
  USERNAME_TOO_LONG = 'USERNAME_TOO_LONG',
  DRAFT_PUBLISH_FAILED = 'DRAFT_PUBLISH_FAILED',

  // Email Address Verification
  UNSUPPORTED_DOMAIN = 'UNSUPPORTED_DOMAIN',
  INCORRECT_VERIFICATION_CODE = 'INCORRECT_VERIFICATION_CODE',
}

// Cancellation type used in personal and library billing
enum CancellationType {
  EndSubscription = 'end_subscription',
  SetAsNonRenewing = 'set_as_non_renewing',
}

// Library
enum LibraryType {
  Personal = 'personal',
  Solo = 'solo',
  Team = 'team',
  School = 'school',
  Classroom = 'classroom',
}

enum LibraryMembershipRole {
  Owner = 'owner',
  Admin = 'admin',
  Maker = 'maker',
  Teacher = 'teacher',
  Student = 'student',
  Contributor = 'contributor',
  Guest = 'guest',
}

enum LibraryMembershipStatus {
  Added = 'added',
  Deactivated = 'deactivated',
  Suspended = 'suspended',
  Left = 'left',
}

enum LibrarySubscriptionStatus {
  OnTrial = 'OnTrial',
  TrialExpired = 'TrialExpired',
  Cancelled = 'Cancelled',
  Paying = 'Paying',
}

enum LibraryMembershipTier {
  Trial = 'trial',
  Neon = 'neon',
  Gold = 'gold',
}

// Notifications

enum NotificationChannelType {
  Push = 'push',
  Email = 'email',
}

enum NotificationType {
  InvitationToCollaborate = 'invitation_to_collaborate',
  Mentioned = 'mentioned',
  PostApprovedDeclined = 'post_approved_declined',
  CommentOnWrittenPost = 'comment_on_written_post',
  ReactionOnWrittenPost = 'reaction_on_written_post',
  NewPostSubmission = 'new_post_submission',
  NewCommentSubmission = 'new_comment_submission',
  PadletRemade = 'padlet_remade',
  PadletFollowed = 'padlet_followed',
  FollowedPadletPost = 'followed_padlet_post',
  FollowedPadletComment = 'followed_padlet_comment',
  FollowedPadletReaction = 'followed_padlet_reaction',
  FollowedAccountNewPadlet = 'followed_account_new_padlet',
  NewFollower = 'new_follower',
  ProductUpdates = 'product_updates',
}

// Surface filters
enum SurfaceFilter {
  All = 'All',
  Published = 'Published',
  Submitted = 'Submitted',
}

// Widgets

enum SnackbarNotificationType {
  success = 'success',
  error = 'error',
}

// Padlet privacy
// kebab-cased wall privacy options
// Use WallPrivacyOption instead
enum WallPrivacy {
  OrgWide = 'org-wide', // org-wide and org-wide-unlisted are the same thing, org-wide is kept for compatibility with the old mobile app
  OrgWideUnlisted = 'org-wide-unlisted',
  OrgWideListed = 'org-wide-listed',
  LibraryWideUnlisted = 'library-wide-unlisted',
  LibraryWideListed = 'library-wide-listed',
  Secret = 'secret', // Visitor can write
  SecretRead = 'secret-read', // Visitor can read
  LoggedInUsersOnly = 'logged-in-users_only',
  Private = 'private',
  PasswordProtected = 'password-protected',
  Public = 'public',
}

// Padlet privacy
// snake-cased wall privacy options. TODO: PRO-5447 Standardise privacy option casing
// Use this instead of WallPrivacy
enum WallPrivacyOption {
  OrgWide = 'org_wide', // org-wide and org-wide-unlisted are the same thing, org-wide is kept for compatibility with the old mobile app
  OrgWideUnlisted = 'org_wide_unlisted',
  OrgWideListed = 'org_wide_listed',
  LibraryWideUnlisted = 'library_wide_unlisted',
  LibraryWideListed = 'library_wide_listed',
  Secret = 'secret', // Visitor can write
  SecretRead = 'secret_read', // Visitor can read
  LoggedInUsersOnly = 'logged_in_users_only',
  Private = 'private',
  PasswordProtected = 'password_protected',
  Public = 'public',
}

enum WallPermission {
  Read = 'read',
  Write = 'write',
  None = 'none',
}

// Tenant Permissions
enum BackpackStudentPermission {
  StudentDefaultWallPrivacy = 'studentDefaultWallPrivacy',
  StudentDefaultWallPermission = 'studentDefaultWallPermission',
  CanStudentsMakeWalls = 'canStudentsMakeWalls',
  CanStudentsMakePrivateWalls = 'canStudentsMakePrivateWalls',
  CanStudentsMakeOrgWideHiddenWalls = 'canStudentsMakeOrgWideHiddenWalls',
  CanStudentsMakeOrgWideDashboardWalls = 'canStudentsMakeOrgWideDashboardWalls',
  CanStudentsMakePasswordWalls = 'canStudentsMakePasswordWalls',
  CanStudentsMakeSecretWalls = 'canStudentsMakeSecretWalls',
  CanStudentsViewGallery = 'canStudentsViewGallery',
}

enum BackpackTeacherPermission {
  TeacherDefaultWallPrivacy = 'teacherDefaultWallPrivacy',
  TeacherDefaultWallPermission = 'teacherDefaultWallPermission',
  CanTeachersMakeWalls = 'canTeachersMakeWalls',
  CanTeachersMakePrivateWalls = 'canTeachersMakePrivateWalls',
  CanTeachersMakeOrgWideHiddenWalls = 'canTeachersMakeOrgWideHiddenWalls',
  CanTeachersMakeOrgWideDashboardWalls = 'canTeachersMakeOrgWideDashboardWalls',
  CanTeachersMakePasswordWalls = 'canTeachersMakePasswordWalls',
  CanTeachersMakeSecretWalls = 'canTeachersMakeSecretWalls',
  CanTeachersAddNewUsers = 'canTeachersAddNewUsers',
  CanTeachersEditUsers = 'canTeachersEditUsers',
}

// School Library Permissions
enum SchoolStudentPermissionBoolean {
  CanMakeWalls = 'canMakeWalls',
  CanMakePrivateWalls = 'canMakePrivateWalls',
  CanMakeLibraryWideHiddenWalls = 'canMakeLibraryWideHiddenWalls',
  CanMakeLibraryWideDashboardWalls = 'canMakeLibraryWideDashboardWalls',
  CanMakePasswordWalls = 'canMakePasswordWalls',
  CanMakeSecretLoggedInVisitorsWalls = 'canMakeSecretLoggedInVisitorsWalls',
  CanMakeSecretAllVisitorsWalls = 'canMakeSecretAllVisitorsWalls',
  CanMakePublicWalls = 'canMakePublicWalls',
  CanViewGallery = 'canViewGallery',
}
enum SchoolPermissionString {
  DefaultWallPrivacy = 'defaultWallPrivacy',
  DefaultWallPermission = 'defaultWallVisitorPermission',
}

const SchoolStudentPermission = { ...SchoolPermissionString, ...SchoolStudentPermissionBoolean }

enum SchoolTeacherPermissionBoolean {
  CanMakeWalls = 'canMakeWalls',
  CanMakePrivateWalls = 'canMakePrivateWalls',
  CanMakeLibraryWideHiddenWalls = 'canMakeLibraryWideHiddenWalls',
  CanMakeLibraryWideDashboardWalls = 'canMakeLibraryWideDashboardWalls',
  CanMakePasswordWalls = 'canMakePasswordWalls',
  CanMakeSecretLoggedInVisitorsWalls = 'canMakeSecretLoggedInVisitorsWalls',
  CanMakeSecretAllVisitorsWalls = 'canMakeSecretAllVisitorsWalls',
  CanMakePublicWalls = 'canMakePublicWalls',
  CanInviteNewStudents = 'canInviteNewStudents',
  CanInviteNewTeachers = 'canInviteNewTeachers',
}

const SchoolTeacherPermission = { ...SchoolPermissionString, ...SchoolTeacherPermissionBoolean }

enum ModerationCategories {
  Sexual = 'sexual',
  Hate = 'hate',
  Violence = 'violence',
  Bullying = 'bullying',
  Weapons = 'weapons',
  ChildExploitation = 'child_exploitation',
  ChildSafety = 'child_safety',
  SelfHarm = 'self_harm',
  Profanity = 'profanity',
  Drugs = 'drugs',
}

// Wall access

enum WallAccessPrivacyType {
  Private = 'private',
  Public = 'public',
  Secret = 'secret',
  PasswordProtected = 'password_protected',
  LoggedInUsersOnly = 'logged_in_users_only',
  OrgWideUnlisted = 'org_wide_unlisted',
  OrgWideListed = 'org_wide_listed',
  LibraryWideUnlisted = 'library_wide_unlisted',
  LibraryWideListed = 'library_wide_listed',
}

enum WallAccessRight {
  None = 'none',
  Read = 'read',
  Write = 'write',
  Edit = 'edit',
  Administer = 'administer',
}

enum WallCollaboratorStatus {
  Requested = 'requested',
  Invited = 'invited',
  Approved = 'approved',
}

enum CommandLevel {
  Padlet = 'Padlet',
  Library = 'Library',
  Account = 'Account',
}

// must match ContentModeration::PostAction::Result in app/services/content_moderation/post_action.rb
enum ParamPostModerationActionResult {
  APPROVE_SUCCESS = 'APPROVE_SUCCESS',
  REJECT_SUCCESS = 'REJECT_SUCCESS',
  LOGIN_REQUIRED = 'LOGIN_REQUIRED',
  MODERATE_FAIL = 'MODERATE_FAIL',
}

// must match ContentModeration::CommentAction::Result in app/services/content_moderation/comment_action.rb
enum ParamCommentModerationActionResult {
  APPROVE_SUCCESS = 'APPROVE_SUCCESS',
  REJECT_SUCCESS = 'REJECT_SUCCESS',
  LOGIN_REQUIRED = 'LOGIN_REQUIRED',
  MODERATE_FAIL = 'MODERATE_FAIL',
}

enum WallVizEnum {
  Canvas = 'free',
  Grid = 'matrix',
  Stream = 'stream',
  Wall = 'grid',
  Map = 'map',
  TimelineV2 = 'timeline_v2',
  // Legacy types
  Timeline = 'timeline',
  /** @deprecated Now is "Wall with sections". */
  Shelf = 'shelf',
  /** @deprecated No longer exists. */
  Table = 'table',
}

enum WallVizName {
  Wall = 'Wall',
  Stream = 'Stream',
  Grid = 'Grid',
  Map = 'Map',
  Canvas = 'Canvas',
  Timeline = 'Timeline',
}

enum ContentModerationCategory {
  Sexual = 'sexual',
  Violence = 'violence',
  Bullying = 'bullying',
  Weapons = 'weapons',
  Drugs = 'drugs',
  Profanity = 'profanity',
  ChildSafety = 'child_safety',
}

enum ContentModerationMode {
  None = 'none',
  Auto = 'auto',
  StudentsOnly = 'students_only',
  All = 'all',
}

enum ContentSafetyEntity {
  Library = 'library',
  Tenant = 'tenant',
}

enum SurfaceOnboardingActionTypes {
  OpenSharePanel = 'Open share panel',
  OpenSettingsPanel = 'Open settings panel',
  GenerateSamplePosts = 'Generate sample posts',
  OpenSlideshow = 'Open slideshow',
  OpenActivityPanel = 'Open activity panel',
  EditModerationSettings = 'Edit moderation settings',
  GetBrowserExtension = 'Get the browser extension',
}

enum WallCreatedFromType {
  gallery = 'gallery_template',
  blank = 'blank',
}

// From UserAccountType::VALID_ACCOUNT_TYPES
enum UserAccountType {
  PERSONAL = 'personal',
  STUDENT = 'student',
  TEACHER = 'teacher',
  STAFF = 'staff',
  BUSINESS = 'business',
}

export {
  ApiErrorCode,
  BackpackStudentPermission,
  BackpackTeacherPermission,
  CancellationType,
  CommandLevel,
  ContentModerationCategory,
  ContentModerationMode,
  ContentSafetyEntity,
  HttpCode,
  LibraryMembershipRole,
  LibraryMembershipStatus,
  LibraryMembershipTier,
  LibrarySubscriptionStatus,
  LibraryType,
  ModerationCategories,
  NotificationChannelType,
  NotificationType,
  ParamPostModerationActionResult,
  ParamCommentModerationActionResult,
  SchoolPermissionString,
  SchoolStudentPermission,
  SchoolStudentPermissionBoolean,
  SchoolTeacherPermission,
  SchoolTeacherPermissionBoolean,
  SnackbarNotificationType,
  SurfaceFilter,
  SurfaceOnboardingActionTypes,
  UserAccountType,
  WallAccessPrivacyType,
  WallAccessRight,
  WallCollaboratorStatus,
  WallCreatedFromType,
  WallPermission,
  WallPrivacy,
  WallPrivacyOption,
  WallVizEnum,
  WallVizName,
}
